import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { todayYMD } from '@/lib/date-presets'
import { ymdVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Payload gửi lên API sửa thông tin — cố ý chỉ có 2 trường thuần mô tả. */
export interface ScentingInNoteInfoValues {
    scenting_date: string
    notes: string
}

// Factory để message i18n được đánh giá lazily (rule Forms của repo).
const createInfoSchema = () =>
    z.object({
        scenting_date: z.string().min(1, m.scl_new_err_scenting_date()),
        notes: z.string().max(2000, m.form_err_notes_too_long()),
    })

interface ScentingInNoteInfoDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Ngày lấy hương đang lưu (RFC3339 từ server). Null = phiếu cũ chưa có. */
    scentingDate?: string | null
    notes?: string | null
    isPending: boolean
    onSubmit: (values: ScentingInNoteInfoValues) => void | Promise<void>
}

/**
 * Popup sửa thông tin của PNK kho lấy hương ĐÃ DUYỆT.
 *
 * Chỉ hai ô: ngày lấy hương + ghi chú. Cố ý KHÔNG dùng lại form tạo phiếu — form
 * đó mang theo danh sách lô và khối lượng, mà phiếu đã duyệt thì những thứ đó đã
 * cộng vào tồn kho nên không được phép đổi.
 */
export function ScentingInNoteInfoDialog({
    open,
    onOpenChange,
    scentingDate,
    notes,
    isPending,
    onSubmit,
}: ScentingInNoteInfoDialogProps) {
    const form = useForm<ScentingInNoteInfoValues>({
        resolver: zodResolver(createInfoSchema()),
        defaultValues: {
            scenting_date: scentingDate ? ymdVN(scentingDate) : todayYMD(),
            notes: notes ?? '',
        },
    })

    const submit = form.handleSubmit((values) => onSubmit(values))

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.scl_in_info_edit_title()}</DialogTitle>
                    <DialogDescription>
                        {m.scl_in_info_edit_desc()}
                    </DialogDescription>
                </DialogHeader>
                <FormProvider {...form}>
                    <form onSubmit={submit} noValidate className="space-y-4">
                        <Field
                            name="scenting_date"
                            label={m.scl_new_scenting_date_label()}
                            required
                        >
                            <Input
                                id="scenting_date"
                                type="date"
                                {...form.register('scenting_date')}
                            />
                        </Field>
                        <Field name="notes" label={m.sl_new_notes()}>
                            <Input
                                id="notes"
                                placeholder={m.sl_new_notes_placeholder()}
                                {...form.register('notes')}
                            />
                        </Field>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isPending}
                            >
                                {m.common_cancel()}
                            </Button>
                            {/* Chỉ disable khi đang gửi (chống bấm 2 lần), KHÔNG
                                disable vì form invalid — người dùng phải bấm được
                                để thấy lỗi cụ thể. */}
                            <Button type="submit" disabled={isPending}>
                                {isPending
                                    ? m.scl_in_info_edit_saving()
                                    : m.common_save()}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
